/**
 * Offline Y.Doc persistence via IndexedDB
 *
 * Saves Y.Doc state keyed by room + epoch. On reconnect:
 *   - Epoch matches  → restore stored state (offline edits preserved)
 *   - Epoch mismatch → discard (room was recreated, state is stale)
 *
 * This avoids the duplication bug that y-indexeddb caused: we control
 * WHEN stored state is applied based on the server's epoch check,
 * rather than auto-merging on mount.
 */

const DB_NAME = 'papan-collab'
const STORE_NAME = 'docs'
const DB_VERSION = 1

interface StoredDoc {
  room: string
  epoch: string
  state: Uint8Array
  updatedAt: number
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'room' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Save Y.Doc state for a room with its epoch.
 * Debounced externally — call after each Y.Doc update.
 */
export async function saveOfflineDoc(room: string, epoch: string, state: Uint8Array): Promise<void> {
  try {
    const db = await openDb()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put({ room, epoch, state, updatedAt: Date.now() } satisfies StoredDoc)
    db.close()
  } catch {
    // IndexedDB unavailable (incognito, storage full) — non-fatal
  }
}

/**
 * Load stored Y.Doc state for a room.
 * Returns the state + epoch, or null if nothing stored.
 */
export async function loadOfflineDoc(room: string): Promise<{ epoch: string; state: Uint8Array; updatedAt: number } | null> {
  try {
    const db = await openDb()
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(room)
      req.onsuccess = () => {
        const doc = req.result as StoredDoc | undefined
        db.close()
        if (doc?.state && doc.epoch) {
          resolve({ epoch: doc.epoch, state: doc.state, updatedAt: doc.updatedAt })
        } else {
          resolve(null)
        }
      }
      req.onerror = () => { db.close(); resolve(null) }
    })
  } catch {
    return null
  }
}

/**
 * Delete stored state for a room (e.g., after clean save or epoch mismatch).
 */
export async function deleteOfflineDoc(room: string): Promise<void> {
  try {
    const db = await openDb()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(room)
    db.close()
  } catch {
    // Non-fatal
  }
}
